import { CLIENT_ERROR_LIMITS, type ClientErrorReport, scrubPersonalData } from '@gestao/shared'

import { appConfig } from '../app/config'

/*
 * Erros não tratados do navegador vão para `POST /client-errors` (sprint-15-spec §2.6): no
 * máximo 5 por sessão da aba, sem dado pessoal (a API remove de novo) e sem esperar resposta.
 */

const SESSION_KEY = 'gestao:client-errors:sent'
let memoryCount = 0

function sentCount(): number {
  try {
    const value = Number(window.sessionStorage.getItem(SESSION_KEY))
    return Number.isFinite(value) ? Math.max(value, memoryCount) : memoryCount
  } catch {
    return memoryCount
  }
}

function markSent(count: number): void {
  memoryCount = count
  try {
    window.sessionStorage.setItem(SESSION_KEY, String(count))
  } catch {
    // sem sessionStorage (aba privada, bloqueio): fica só o contador em memória
  }
}

/** Só para os testes: zera o contador da sessão. */
export function resetClientErrorBudget(): void {
  memoryCount = 0
  try {
    window.sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // ignorado
  }
}

const cut = (value: string, max: number) => (value.length > max ? value.slice(0, max) : value)

function describe(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      message: `${error.name}: ${error.message}`,
      ...(error.stack ? { stack: error.stack } : {}),
    }
  }
  if (typeof error === 'string') return { message: error }
  try {
    return { message: JSON.stringify(error) ?? String(error) }
  } catch {
    return { message: String(error) }
  }
}

/** Monta o corpo dentro dos tetos da API (1 KB no total). */
export function buildClientErrorReport(error: unknown, route: string): ClientErrorReport {
  const { message, stack } = describe(error)
  const report: ClientErrorReport = {
    message: cut(
      scrubPersonalData(message).trim() || 'Erro sem mensagem',
      CLIENT_ERROR_LIMITS.message,
    ),
    route: cut(route.split(/[?#]/)[0] || '/', CLIENT_ERROR_LIMITS.route),
    version: cut(appConfig.gitSha ?? 'dev', CLIENT_ERROR_LIMITS.version),
  }
  if (stack) {
    // O que sobrar do 1 KB depois dos outros campos (com folga para a chave e as aspas).
    const room = Math.max(0, CLIENT_ERROR_LIMITS.maxBodyBytes - byteLength(report) - 16)
    let candidate = cut(scrubPersonalData(stack), Math.min(CLIENT_ERROR_LIMITS.stack, room))
    while (
      candidate &&
      byteLength({ ...report, stack: candidate }) > CLIENT_ERROR_LIMITS.maxBodyBytes
    )
      candidate = candidate.slice(0, -32)
    if (candidate) report.stack = candidate
  }
  return report
}

const encoder = new TextEncoder()
const byteLength = (value: unknown) => encoder.encode(JSON.stringify(value)).length

/** Envia um erro; devolve false quando o limite da sessão já foi atingido. */
export function reportClientError(error: unknown, route = window.location.pathname): boolean {
  const count = sentCount()
  if (count >= CLIENT_ERROR_LIMITS.perSession) return false
  markSent(count + 1)
  const body = JSON.stringify(buildClientErrorReport(error, route))
  try {
    void fetch(`${appConfig.apiUrl}/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // envio de diagnóstico nunca pode quebrar a tela
  }
  return true
}

/** `window.onerror` e `unhandledrejection`. Devolve a função que remove os ouvintes. */
export function installClientErrorReporting(): () => void {
  const onError = (event: ErrorEvent) => {
    reportClientError(event.error ?? event.message)
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    reportClientError(event.reason)
  }
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
  return () => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}
